/**
 * useBlockOperations — Handles block insert, delete, and undo operations.
 *
 * Extracted from PageEditorPage.tsx to stay under the 800-line limit.
 * Insert and delete are STRUCTURAL operations — they modify the blocks[]
 * array and trigger PAGE_CONFIG_UPDATE (no cosmetic skip).
 *
 * Undo stack stores deep-cloned block arrays (max 20 entries).
 *
 * Part of Plan 145 — Block Editor Customizer, Epic 03.
 */

import { useCallback, useEffect, useRef, type MutableRefObject } from 'react'
import { createDefaultBlock, WIDGET_REGISTRY } from '@zaviago/storefront-config'
import type { BlockNode, BlockType, SectionEnvelope } from '@zaviago/storefront-config'
import type { EditorSection } from './usePageSections'
import type { SelectedBlockInfo } from './useBlockSelection'

// ═══════════════════════════════════════════════════════════════════════════
// STRUCTURAL BLOCKS — prompt before deletion
// ═══════════════════════════════════════════════════════════════════════════

/** Block IDs considered structural — user gets a confirmation prompt before delete */
const STRUCTURAL_BLOCK_IDS = new Set([
  'topbar-logo',
  'topbar-search',
  'navbar-categories',
  'navbar-cart',
  'footer-logo',
  'copyright-text',
])

/** Minimum blocks: sections must retain at least 1 block after delete */
const MIN_BLOCKS = 1

// ═══════════════════════════════════════════════════════════════════════════
// HELPER: Map sectionPath to editorId (same as useBlockSelection)
// ═══════════════════════════════════════════════════════════════════════════

function sectionPathToEditorId(
  sections: EditorSection[],
  sectionPath: string,
): string | null {
  const parts = sectionPath.split('.').map(Number)
  let current: EditorSection | undefined = sections[parts[0]]
  for (let i = 1; i < parts.length && current?.children; i++) {
    current = current.children[parts[i]]
  }
  return current?._editorId ?? null
}

/** Deep-clone a blocks array to prevent mutation corruption in undo stack */
function deepCloneBlocks(blocks: BlockNode[]): BlockNode[] {
  return blocks.map(b => ({
    ...b,
    content: { ...b.content },
    layout: { ...b.layout },
    appearance: b.appearance ? structuredClone(b.appearance) : undefined,
  }))
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface SingleUndoEntry {
  type?: 'single'
  sectionEditorId: string
  previousBlocks: BlockNode[]
}

interface CompoundUndoEntry {
  type: 'compound'
  entries: Array<{ sectionEditorId: string; previousBlocks: BlockNode[] }>
}

/** Undo entry for gap drops: restores source blocks + removes the auto-created section (Plan 150 Epic 04) */
interface GapDropUndoEntry {
  type: 'gap-drop'
  sourceEntry: { sectionEditorId: string; previousBlocks: BlockNode[] }
  createdSectionEditorId: string
}

type UndoEntry = SingleUndoEntry | CompoundUndoEntry | GapDropUndoEntry

interface UseBlockOperationsOpts {
  sectionsRef: MutableRefObject<EditorSection[]>
  findByEditorId: (id: string) => { section: SectionEnvelope } | null
  updateSection: (editorId: string, patch: Partial<SectionEnvelope>) => void
  deselectBlock: () => void
  selectedBlock: SelectedBlockInfo | null
  /** Remove a section by editorId — needed for gap-drop undo (Plan 150 Epic 04) */
  removeSection?: (editorId: string) => void
}

const MAX_UNDO = 20

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useBlockOperations(opts: UseBlockOperationsOpts) {
  const undoStackRef = useRef<UndoEntry[]>([])

  const handleInsertBlock = useCallback(
    (sectionPath: string, blockType: BlockType, insertAtIndex: number, blockIdPrefix?: string) => {
      const editorId = sectionPathToEditorId(opts.sectionsRef.current, sectionPath)
      if (!editorId) return

      const lookup = opts.findByEditorId(editorId)
      if (!lookup?.section) return

      const currentBlocks = lookup.section.blocks ?? []

      // Push undo entry (deep clone)
      undoStackRef.current.push({
        type: 'single',
        sectionEditorId: editorId,
        previousBlocks: deepCloneBlocks(currentBlocks),
      })
      if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift()

      // Generate block ID with prefix if provided (ensures correct sub-group membership)
      const rawId = crypto.randomUUID().slice(0, 8)
      const blockId = blockIdPrefix ? `${blockIdPrefix}-${rawId}` : rawId
      const newBlock = createDefaultBlock(blockType, blockId)
      // Set groupId from prefix or inferred from ID (Plan 148 — cross-group reorder)
      newBlock.layout.groupId = blockIdPrefix
        ? blockIdPrefix.split('-')[0]
        : newBlock.id.split('-')[0]

      // Map sub-group relative index → absolute index in the full blocks array
      let absoluteIndex = insertAtIndex
      if (blockIdPrefix) {
        // Find blocks matching this prefix, sort by order
        const sorted = [...currentBlocks].sort((a, b) => a.layout.order - b.layout.order)
        const prefixBlocks = sorted.filter(b => b.id.startsWith(blockIdPrefix))
        if (insertAtIndex >= prefixBlocks.length) {
          // Insert after the last block in this prefix group
          const lastPrefixBlock = prefixBlocks[prefixBlocks.length - 1]
          absoluteIndex = lastPrefixBlock
            ? sorted.indexOf(lastPrefixBlock) + 1
            : currentBlocks.length
        } else {
          // Insert before the Nth prefix block
          const targetBlock = prefixBlocks[insertAtIndex]
          absoluteIndex = targetBlock ? sorted.indexOf(targetBlock) : currentBlocks.length
        }
      }

      // Splice into blocks array at the absolute index
      const blocks = [...currentBlocks]
      blocks.splice(absoluteIndex, 0, newBlock)

      // Re-index layout.order to match array positions
      const reindexed = blocks.map((b, i) => ({
        ...b,
        layout: { ...b.layout, order: i },
      }))

      opts.updateSection(editorId, { blocks: reindexed })
    },
    [opts],
  )

  const handleRemoveBlock = useCallback(
    (sectionPath: string, blockId: string) => {
      const editorId = sectionPathToEditorId(opts.sectionsRef.current, sectionPath)
      if (!editorId) return

      const lookup = opts.findByEditorId(editorId)
      if (!lookup?.section?.blocks) return

      // Minimum block validation — must retain at least MIN_BLOCKS
      if (lookup.section.blocks.length <= MIN_BLOCKS) return

      // Structural block confirmation — synchronous window.confirm
      if (STRUCTURAL_BLOCK_IDS.has(blockId)) {
        const confirmed = window.confirm(
          `This is a structural block (${blockId}). Are you sure you want to delete it?`
        )
        if (!confirmed) return
      }

      // Push undo entry (deep clone)
      undoStackRef.current.push({
        type: 'single',
        sectionEditorId: editorId,
        previousBlocks: deepCloneBlocks(lookup.section.blocks),
      })
      if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift()

      // Remove block and re-index
      const remaining = lookup.section.blocks
        .filter((b) => b.id !== blockId)
        .map((b, i) => ({ ...b, layout: { ...b.layout, order: i } }))

      opts.updateSection(editorId, { blocks: remaining })

      // Deselect if the removed block was selected
      if (opts.selectedBlock?.blockId === blockId) {
        opts.deselectBlock()
      }
    },
    [opts],
  )

  const handleUndo = useCallback(() => {
    const entry = undoStackRef.current.pop()
    if (!entry) return
    if (entry.type === 'compound') {
      for (const sub of entry.entries) {
        opts.updateSection(sub.sectionEditorId, { blocks: sub.previousBlocks })
      }
    } else if (entry.type === 'gap-drop') {
      // Restore source section blocks + remove the auto-created section (Plan 150 Epic 04)
      opts.updateSection(entry.sourceEntry.sectionEditorId, { blocks: entry.sourceEntry.previousBlocks })
      opts.removeSection?.(entry.createdSectionEditorId)
    } else {
      // SingleUndoEntry (type: 'single' or undefined)
      opts.updateSection(entry.sectionEditorId, { blocks: entry.previousBlocks })
    }
    opts.deselectBlock()
  }, [opts])

  const handleResetToDefault = useCallback(
    (sectionPath: string) => {
      const editorId = sectionPathToEditorId(opts.sectionsRef.current, sectionPath)
      if (!editorId) return

      const lookup = opts.findByEditorId(editorId)
      if (!lookup?.section) return

      const widgetDef = WIDGET_REGISTRY[lookup.section.type]
      const defaults = widgetDef?.defaultBlocks
      if (!defaults?.length) return

      const confirmed = window.confirm(
        'Reset all blocks to their default layout? This cannot be undone.'
      )
      if (!confirmed) return

      // Push undo entry
      if (lookup.section.blocks) {
        undoStackRef.current.push({
          type: 'single',
          sectionEditorId: editorId,
          previousBlocks: deepCloneBlocks(lookup.section.blocks),
        })
        if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift()
      }

      // structuredClone to prevent mutation of module-level singleton
      opts.updateSection(editorId, { blocks: structuredClone(defaults) })
      opts.deselectBlock()
    },
    [opts],
  )

  // Cmd+Z / Ctrl+Z listener for undo (on parent window)
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'z')) return
      // Don't intercept when typing in input/textarea/contenteditable
      const tag = document.activeElement?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (document.activeElement?.getAttribute('contenteditable')) return
      // Only undo if there are entries in the stack
      if (undoStackRef.current.length === 0) return

      e.preventDefault()
      handleUndo()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleUndo])

  /** Push an undo entry from external callers (e.g. block reorder in useBlockSelection) */
  const pushUndoEntry = useCallback(
    (sectionEditorId: string, previousBlocks: BlockNode[]) => {
      undoStackRef.current.push({
        type: 'single',
        sectionEditorId,
        previousBlocks: deepCloneBlocks(previousBlocks),
      })
      if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift()
    },
    [],
  )

  /** Push a compound undo entry for cross-section moves (restores multiple sections atomically) */
  const pushCompoundUndo = useCallback(
    (entries: Array<{ sectionEditorId: string; previousBlocks: BlockNode[] }>) => {
      undoStackRef.current.push({
        type: 'compound',
        entries: entries.map(e => ({
          sectionEditorId: e.sectionEditorId,
          previousBlocks: deepCloneBlocks(e.previousBlocks),
        })),
      })
      if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift()
    },
    [],
  )

  /** Push a gap-drop undo entry: restores source blocks + removes auto-created section (Plan 150 Epic 04) */
  const pushGapDropUndo = useCallback(
    (sourceEditorId: string, previousBlocks: BlockNode[], createdSectionEditorId: string) => {
      undoStackRef.current.push({
        type: 'gap-drop',
        sourceEntry: { sectionEditorId: sourceEditorId, previousBlocks: deepCloneBlocks(previousBlocks) },
        createdSectionEditorId,
      })
      if (undoStackRef.current.length > MAX_UNDO) undoStackRef.current.shift()
    },
    [],
  )

  return { handleInsertBlock, handleRemoveBlock, handleUndo, handleResetToDefault, pushUndoEntry, pushCompoundUndo, pushGapDropUndo }
}
